import React, { useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

const CSS = `
.tb-land *, .tb-land *::before, .tb-land *::after { box-sizing: border-box; margin: 0; padding: 0; }
.tb-land {
  --bg:       #eef2f9;
  --surface:  rgba(255,255,255,.82);
  --surface2: rgba(255,255,255,.55);
  --nav-bg:   rgba(255,255,255,.90);
  --border:   rgba(0,10,60,.08);
  --border2:  rgba(0,10,60,.05);
  --text:     #1a2540;
  --text2:    #5a6880;
  --text3:    #9aaabf;
  --shadow:   rgba(20,40,120,.08);
  --shadow2:  rgba(20,40,120,.18);
  --hover:    rgba(0,10,60,.05);
  --mesh1:    rgba(99,138,255,.13);
  --mesh2:    rgba(52,211,153,.08);
  --mesh3:    rgba(167,139,250,.09);
  --a1: #2563eb; --a2: #7c3aed;
  --a-shadow: rgba(37,99,235,.3);
  --a-shadow2:rgba(37,99,235,.48);
  --stripe:   linear-gradient(90deg,#2563eb,#7c3aed,#06b6d4);
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}
.tb-mesh {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background:
    radial-gradient(ellipse 70% 60% at 20% 20%, var(--mesh1) 0%, transparent 55%),
    radial-gradient(ellipse 55% 70% at 84% 12%, var(--mesh2) 0%, transparent 55%),
    radial-gradient(ellipse 65% 55% at 52% 88%, var(--mesh3) 0%, transparent 55%),
    var(--bg);
}
.tb-noise {
  position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: .022;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 256px;
}
.tb-container { max-width: 1100px; margin: 0 auto; padding: 0 28px; }
.tb-page { position: relative; z-index: 1; }

.tb-stripe { position: fixed; top: 0; left: 0; right: 0; height: 3px; background: var(--stripe); z-index: 200; }

.tb-nav {
  position: fixed; top: 3px; left: 0; right: 0; z-index: 100;
  height: 64px;
  background: var(--nav-bg);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  backdrop-filter: blur(24px) saturate(160%);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 2px 16px var(--shadow);
}
.tb-nav-inner {
  max-width: 1100px; margin: 0 auto; padding: 0 28px;
  height: 100%; display: flex; align-items: center;
}
.tb-brand { display: flex; align-items: center; gap: 11px; text-decoration: none; }
.tb-brand-mark {
  width: 38px; height: 38px; border-radius: 11px;
  background: linear-gradient(135deg, var(--a1), var(--a2));
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; font-weight: 900; color: white;
  box-shadow: 0 4px 14px var(--a-shadow);
}
.tb-brand-name { font-size: 18px; font-weight: 900; letter-spacing: -.02em; color: var(--text); }
.tb-nav-links { display: flex; align-items: center; gap: 2px; margin-left: 36px; }
.tb-nav-link {
  padding: 7px 14px; border-radius: 8px; font-size: 13px; font-weight: 600;
  color: var(--text2); text-decoration: none; transition: all .15s;
  border: none; background: transparent; cursor: pointer; font-family: inherit;
}
.tb-nav-link:hover { color: var(--text); background: var(--hover); }
.tb-nav-right { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.tb-btn-signin {
  padding: 8px 18px; border-radius: 10px;
  background: transparent; border: 1.5px solid var(--border);
  color: var(--text2); font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: all .2s; text-decoration: none;
  display: flex; align-items: center;
}
.tb-btn-signin:hover { color: var(--text); background: var(--hover); }

.tb-hero { padding: 160px 0 100px; text-align: center; }
.tb-hero-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 18px; border-radius: 20px;
  background: var(--surface);
  -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  font-size: 12px; font-weight: 700; color: var(--text3);
  margin-bottom: 30px; box-shadow: 0 2px 10px var(--shadow);
  animation: tbFadeUp .5s ease both;
}
.tb-badge-dot {
  width: 6px; height: 6px; border-radius: 50%; background: var(--a1);
  box-shadow: 0 0 6px var(--a1); animation: tbPulse 2.2s infinite;
}
@keyframes tbPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.5)} }
.tb-hero-title {
  font-size: 62px; font-weight: 900; letter-spacing: -.04em; line-height: 1.06;
  color: var(--text); margin-bottom: 22px; max-width: 740px;
  margin-left: auto; margin-right: auto;
  animation: tbFadeUp .5s .07s ease both;
}
.tb-hero-title .tb-accent {
  background: var(--stripe);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.tb-hero-sub {
  font-size: 18px; color: var(--text2); line-height: 1.65;
  max-width: 500px; margin: 0 auto 44px;
  animation: tbFadeUp .5s .14s ease both;
}
.tb-hero-actions {
  display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap;
  animation: tbFadeUp .5s .20s ease both;
}
.tb-btn-hero-register {
  padding: 15px 36px; border-radius: 13px;
  background: linear-gradient(135deg, var(--a1), var(--a2));
  color: white; font-size: 15px; font-weight: 800;
  border: none; cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 22px var(--a-shadow);
  transition: all .2s; text-decoration: none;
  display: flex; align-items: center; gap: 9px;
  position: relative; overflow: hidden;
}
.tb-btn-hero-register::before {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,.15), transparent);
  opacity: 0; transition: opacity .2s;
}
.tb-btn-hero-register:hover { box-shadow: 0 10px 30px var(--a-shadow2); transform: translateY(-2px); }
.tb-btn-hero-register:hover::before { opacity: 1; }
.tb-btn-hero-signin {
  padding: 15px 26px; border-radius: 13px;
  background: var(--surface);
  -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  color: var(--text2); font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: all .2s; text-decoration: none;
  display: flex; align-items: center; gap: 7px;
}
.tb-btn-hero-signin:hover { color: var(--text); background: var(--surface); box-shadow: 0 4px 16px var(--shadow2); }
.tb-trust {
  display: flex; align-items: center; justify-content: center; gap: 20px;
  margin-top: 24px; font-size: 12px; color: var(--text3); font-weight: 600;
  animation: tbFadeUp .5s .30s ease both;
}
.tb-trust-item { display: flex; align-items: center; gap: 5px; }
.tb-trust-sep { width: 3px; height: 3px; border-radius: 50%; background: var(--border); }

.tb-modules { padding: 80px 0; }
.tb-section-label {
  text-align: center; font-size: 11px; font-weight: 800; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a1); margin-bottom: 14px;
}
.tb-section-title {
  text-align: center; font-size: 36px; font-weight: 900; letter-spacing: -.03em;
  color: var(--text); margin-bottom: 10px;
}
.tb-section-sub {
  text-align: center; font-size: 15px; color: var(--text2);
  max-width: 440px; margin: 0 auto 44px; line-height: 1.6;
}
.tb-modules-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
  max-width: 820px; margin: 0 auto;
}
.tb-mod-card {
  background: var(--surface);
  -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);
  border: 1px solid var(--border); border-radius: 16px;
  padding: 22px 20px; text-align: center; transition: all .2s;
  position: relative; overflow: hidden;
  cursor: default;
}
.tb-mod-card:hover { transform: translateY(-3px); box-shadow: 0 10px 32px var(--shadow2); border-color: rgba(37,99,235,.18); }
.tb-mod-card::after {
  content: '';
  position: absolute; inset: 0; border-radius: 16px;
  background: radial-gradient(circle at var(--mx,50%) var(--my,50%), rgba(37,99,235,.12) 0%, transparent 65%);
  opacity: 0; transition: opacity .3s;
  pointer-events: none;
}
.tb-mod-card:hover::after { opacity: 1; }
.tb-mod-icon { font-size: 28px; margin-bottom: 10px; }
.tb-mod-name { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.tb-mod-desc { font-size: 11px; color: var(--text3); line-height: 1.5; }

.tb-cta-section { padding: 60px 0 100px; }
.tb-cta-banner {
  background: #1a2540;
  border-radius: 22px; padding: 60px 48px;
  text-align: center; position: relative; overflow: hidden;
  box-shadow: 0 16px 52px rgba(20,40,120,.22);
  max-width: 760px; margin: 0 auto;
}
.tb-cta-banner::before {
  content: ''; position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 80% at 10% 50%, rgba(99,138,255,.08) 0%, transparent 55%),
    radial-gradient(ellipse 50% 60% at 90% 20%, rgba(167,139,250,.06) 0%, transparent 55%);
  pointer-events: none;
}
.tb-cta-title { font-size: 36px; font-weight: 900; letter-spacing: -.03em; color: #e2e8f0; margin-bottom: 12px; position: relative; }
.tb-cta-sub { font-size: 16px; color: rgba(226,232,240,.55); margin-bottom: 32px; line-height: 1.6; position: relative; }
.tb-cta-actions { display: flex; align-items: center; justify-content: center; gap: 12px; position: relative; flex-wrap: wrap; }
.tb-btn-cta-white {
  padding: 13px 30px; border-radius: 12px;
  background: linear-gradient(135deg, var(--a1), var(--a2)); color: white;
  font-size: 14px; font-weight: 800; border: none; cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 22px var(--a-shadow);
  transition: all .2s; text-decoration: none; display: flex; align-items: center; gap: 8px;
}
.tb-btn-cta-white:hover { box-shadow: 0 10px 30px var(--a-shadow2); transform: translateY(-2px); }
.tb-btn-cta-ghost {
  padding: 13px 26px; border-radius: 12px;
  background: transparent; color: rgba(226,232,240,.65);
  border: 1.5px solid rgba(226,232,240,.18);
  font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit;
  transition: all .2s; text-decoration: none;
}
.tb-btn-cta-ghost:hover { border-color: rgba(226,232,240,.4); background: rgba(255,255,255,.05); color: #e2e8f0; }
.tb-cta-note { font-size: 12px; color: rgba(226,232,240,.3); margin-top: 16px; position: relative; }

.tb-footer {
  border-top: 1px solid var(--border);
  background: var(--surface);
  -webkit-backdrop-filter: blur(16px); backdrop-filter: blur(16px);
  padding: 24px 0;
}
.tb-footer-inner {
  max-width: 1100px; margin: 0 auto; padding: 0 28px;
  display: flex; align-items: center; justify-content: space-between;
}
.tb-footer-left { display: flex; align-items: center; gap: 14px; }
.tb-footer-brand { display: flex; align-items: center; gap: 8px; }
.tb-footer-mark {
  width: 26px; height: 26px; border-radius: 7px;
  background: linear-gradient(135deg,var(--a1),var(--a2));
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 900; color: white;
}
.tb-footer-name { font-size: 14px; font-weight: 800; color: var(--text); letter-spacing: -.01em; }
.tb-footer-copy { font-size: 12px; color: var(--text3); padding-left: 14px; border-left: 1px solid var(--border2); }
.tb-footer-links { display: flex; gap: 18px; }
.tb-footer-link { font-size: 12px; color: var(--text3); text-decoration: none; transition: color .15s; }
.tb-footer-link:hover { color: var(--a1); }

@keyframes tbFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
.tb-reveal { opacity: 0; transform: translateY(20px); transition: opacity .5s, transform .5s; }
.tb-reveal.tb-visible { opacity: 1; transform: none; }

#tb-cursor-glow {
  position: fixed; pointer-events: none; z-index: 0;
  width: 500px; height: 500px; border-radius: 50%;
  background: radial-gradient(circle, rgba(37,99,235,.12) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  transition: opacity .3s; opacity: 0;
}
@media (max-width: 640px) {
  .tb-hero-title { font-size: 36px; }
  .tb-modules-grid { grid-template-columns: repeat(2, 1fr); }
  .tb-nav-links { display: none; }
  .tb-cta-banner { padding: 40px 24px; }
  .tb-cta-title { font-size: 26px; }
}
`;

const MODULES = [
  { icon: '⏱', name: 'Timesheets',   desc: 'Log hours & track TOIL automatically' },
  { icon: '✅', name: 'Approvals',    desc: 'One-click weekly report approval' },
  { icon: '💰', name: 'Expenses',     desc: 'Claims, mileage and payroll in one place' },
  { icon: '👥', name: 'HR & People',  desc: 'Org chart, profiles and leave balances' },
  { icon: '📁', name: 'Documents',    desc: 'Policies, contracts and e-signatures' },
  { icon: '🤝', name: 'CRM',          desc: 'Contacts, organisations and projects' },
  { icon: '📅', name: 'Booking',      desc: 'Rooms, resources and equipment' },
  { icon: '🎓', name: 'Training',     desc: 'Courses, quizzes and certifications' },
];

const Home = () => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const glowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'tb-land-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('tb-visible'); });
    }, { threshold: 0.1 });
    document.querySelectorAll('.tb-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;
    const onMove = (e: MouseEvent) => {
      glow.style.left = e.clientX + 'px';
      glow.style.top  = e.clientY + 'px';
      glow.style.opacity = '1';
    };
    const onLeave = () => { glow.style.opacity = '0'; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const onCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const r = card.getBoundingClientRect();
    card.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
    card.style.setProperty('--my', ((e.clientY - r.top)  / r.height * 100) + '%');
  };

  if (auth) return null;

  return (
    <div className="tb-land">
      <div ref={glowRef} id="tb-cursor-glow" />
      <div className="tb-mesh" />
      <div className="tb-noise" />
      <div className="tb-stripe" />

      {/* NAV */}
      <nav className="tb-nav">
        <div className="tb-nav-inner">
          <a href="/" className="tb-brand">
            <div className="tb-brand-mark">T</div>
            <div className="tb-brand-name">TimeBerlo</div>
          </a>
          <div className="tb-nav-links">
            <a href="#modules" className="tb-nav-link">Features</a>
            <a href="#contact" className="tb-nav-link">Contact</a>
          </div>
          <div className="tb-nav-right">
            <Link to="/login" className="tb-btn-signin">Sign in</Link>
          </div>
        </div>
      </nav>

      <div className="tb-page">

        {/* HERO */}
        <section className="tb-hero">
          <div className="tb-container">
            <div className="tb-hero-badge">
              <div className="tb-badge-dot" />
              HR & Time Management Platform
            </div>
            <h1 className="tb-hero-title">
              Manage your team<br />
              <span className="tb-accent">smarter and faster</span>
            </h1>
            <p className="tb-hero-sub">
              Timesheets, approvals, expenses, HR documents and CRM — all in one portal.
            </p>
            <div className="tb-hero-actions">
              <Link to="/register" className="tb-btn-hero-register">
                Register your company ✨
              </Link>
              <Link to="/login" className="tb-btn-hero-signin">
                Sign in to portal →
              </Link>
            </div>
            <div className="tb-trust">
              <div className="tb-trust-item">🔒 256-bit SSL</div>
              <div className="tb-trust-sep" />
              <div className="tb-trust-item">🛡 GDPR compliant</div>
              <div className="tb-trust-sep" />
              <div className="tb-trust-item">💳 No credit card required</div>
            </div>
          </div>
        </section>

        {/* MODULES */}
        <section className="tb-modules tb-reveal" id="modules">
          <div className="tb-container">
            <div className="tb-section-label">What's included</div>
            <h2 className="tb-section-title">Everything your team needs</h2>
            <p className="tb-section-sub">One portal for all HR operations — no integrations, no data silos.</p>
            <div className="tb-modules-grid">
              {MODULES.map(m => (
                <div key={m.name} className="tb-mod-card" onMouseMove={onCardMouseMove}>
                  <div className="tb-mod-icon">{m.icon}</div>
                  <div className="tb-mod-name">{m.name}</div>
                  <div className="tb-mod-desc">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="tb-cta-section tb-reveal" id="contact">
          <div className="tb-container">
            <div className="tb-cta-banner">
              <h2 className="tb-cta-title">Ready to get started?</h2>
              <p className="tb-cta-sub">Register your company for free, or sign in if you already have an account.</p>
              <div className="tb-cta-actions">
                <Link to="/register" className="tb-btn-cta-white">Register company — free ✨</Link>
                <a href="mailto:support@timeberlo.com" className="tb-btn-cta-ghost">Contact us</a>
              </div>
              <div className="tb-cta-note">🔒 Secure · GDPR compliant · No credit card needed</div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="tb-footer">
          <div className="tb-footer-inner">
            <div className="tb-footer-left">
              <div className="tb-footer-brand">
                <div className="tb-footer-mark">T</div>
                <div className="tb-footer-name">TimeBerlo</div>
              </div>
              <div className="tb-footer-copy">© 2026 All rights reserved.</div>
            </div>
            <div className="tb-footer-links">
              <a href="mailto:support@timeberlo.com" className="tb-footer-link">Contact</a>
              <a href="#" className="tb-footer-link">Privacy Policy</a>
              <a href="#" className="tb-footer-link">Terms</a>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Home;
